// JobTechPayStrip — Phase 10 R3
//
// Per-row pay-side display. Visible to OWNER/MANAGER only.
// Rendered only when labor_engine_enabled && ui_technician_pay are ON.
//
// PAY-SIDE ONLY. Never feeds sell (R2) or cost (R4) engines.

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { UserCircle2, Loader2, DollarSign } from 'lucide-react';
import type { TicketTechPayLine } from '@/hooks/labor/useTicketTechPayLines';
import { useComputeJobTechPay } from '@/hooks/labor/useComputeJobTechPay';
import { useUserRole } from '@/hooks/useUserRole';

const fmt = (n: number) =>
  n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });

interface Props {
  ticketId: string;
  line: TicketTechPayLine;
}

function statusBadge(status: TicketTechPayLine['tech_pay_status']) {
  switch (status) {
    case 'computed':
      return { label: 'Flat-rate', variant: 'default' as const };
    case 'deferred_hourly':
      return { label: 'Deferred', variant: 'secondary' as const };
    case 'no_config':
      return { label: 'No config', variant: 'outline' as const };
    default:
      return { label: 'Pay', variant: 'outline' as const };
  }
}

export function JobTechPayStrip({ ticketId, line }: Props) {
  const { isManagerView } = useUserRole();
  const computeMut = useComputeJobTechPay();

  // Defense-in-depth: hide for non-managers (RLS would also block reads).
  if (!isManagerView) return null;

  // ── Uncomputed row → "Compute tech pay" affordance ────────────────────
  if (line.is_uncomputed) {
    return (
      <div className="mt-1.5 flex items-center justify-between rounded-md border border-dashed bg-muted/20 px-2.5 py-1.5 text-xs">
        <span className="text-muted-foreground flex items-center gap-1.5">
          <UserCircle2 className="h-3.5 w-3.5" />
          Tech pay not computed
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          disabled={computeMut.isPending}
          onClick={() => computeMut.mutate({ ticketId, jobId: line.job_id })}
        >
          {computeMut.isPending ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <DollarSign className="h-3 w-3 mr-1" />
          )}
          Compute pay
        </Button>
      </div>
    );
  }

  const badge = statusBadge(line.tech_pay_status);
  const reasonText = line.tech_pay_reason ?? '';

  // ── Computed flat-rate row ────────────────────────────────────────────
  if (line.tech_pay_status === 'computed' && line.tech_pay_amount != null) {
    const hours = (line.tech_pay_minutes ?? 0) / 60;
    const rate = line.tech_pay_rate_applied ?? 0;
    return (
      <div className="mt-1.5 flex items-center justify-between gap-2 rounded-md bg-background/60 px-2.5 py-1.5 text-xs">
        <div className="flex items-center gap-2 min-w-0">
          <UserCircle2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-foreground truncate">
            <span className="tabular-nums">{hours.toFixed(2)} hrs</span>
            <span className="text-muted-foreground"> × </span>
            <span className="tabular-nums">{fmt(rate)}/hr</span>
            <span className="text-muted-foreground"> = </span>
            <span className="font-medium tabular-nums">
              {fmt(line.tech_pay_amount)}
            </span>
          </span>
        </div>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant={badge.variant} className="text-[10px] h-5 px-1.5">
                {badge.label}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              {reasonText}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  // ── deferred_hourly OR no_config row (no $ shown) ─────────────────────
  const headlineText =
    line.tech_pay_status === 'deferred_hourly'
      ? 'Hourly pay mode — per-job pay not computed in R3'
      : 'No pay config for assigned technician';

  return (
    <div className="mt-1.5 flex items-center justify-between gap-2 rounded-md bg-muted/20 px-2.5 py-1.5 text-xs">
      <div className="flex items-center gap-2 min-w-0">
        <UserCircle2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-muted-foreground truncate">{headlineText}</span>
      </div>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant={badge.variant} className="text-[10px] h-5 px-1.5">
              {badge.label}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-xs">
            {reasonText}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
